import type { FragmentType } from "../../../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../../../__generated__/gql";
import {
  BarbecueIcon,
  BathtubIcon,
  DirectionsCarIcon,
  ElectricalServicesIcon,
  InfoIcon,
  MonitorIcon,
} from "../../../../../../_components/dx-icons";
import FacilityDetailModal from "../../../../../facility/_components/facility-detail-modal";
import { DxImageInfoButton } from "../../../../_components/dx-image-info-button";

export const SiteAboutFacilitiesFragment = graphql(`
  fragment SiteAboutFacilities_Site on Site {
    content {
      parkingCapacity
      parkingInfo
      mapImageUrl
    }
    facilities {
      id
      type
      name
      description
      ...FacilityDetailModalFragment
    }
  }
`);

type IconComponent = (props: { className?: string }) => React.ReactNode;

// FacilityType (生成 enum 値) → 行頭アイコン。 全 5 種を明示的に対応させる
// (未登録だと ⓘ にフォールバックしてしまうため。 Barbecue が抜けていた不具合の修正)。
const FACILITY_ICONS: Record<string, IconComponent> = {
  OnsenAndSauna: BathtubIcon,
  CoWorkingSpace: MonitorIcon,
  RentalCar: DirectionsCarIcon,
  EvCharger: ElectricalServicesIcon,
  Barbecue: BarbecueIcon,
};

/** 1 行ぶんの付帯情報 (駐車場 / 付帯サービス) を描画する。 */
function FeatureRow({
  Icon,
  label,
  description,
  trailing,
}: {
  Icon: IconComponent;
  label: string;
  description?: string | null;
  /** 行末に置くアクション。 駐車場は拠点案内図の ⓘ、 付帯サービスは設備詳細の ⓘ。 */
  trailing?: React.ReactNode;
}) {
  return (
    <li className="dx-body-1 flex items-start gap-[16px] py-[8px]">
      {/* dx-icon-baseline: アイコンをラベル 1 行目の中央に揃える */}
      <span className="dx-icon-baseline text-dx-text-default">
        <Icon className="size-[20px]" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-dx-text-default">{label}</p>
        {description && (
          <p className="dx-body-3 text-dx-text-sub mt-[4px] font-normal">
            {description}
          </p>
        )}
      </div>
      {trailing}
    </li>
  );
}

export function SiteAboutFacilities({
  site: siteData,
}: {
  site: FragmentType<typeof SiteAboutFacilitiesFragment>;
}) {
  const site = useFragment(SiteAboutFacilitiesFragment, siteData);
  const { parkingCapacity, parkingInfo, mapImageUrl } = site.content;
  const facilities = site.facilities;
  const hasParking = Boolean(parkingCapacity || parkingInfo);
  const hasFeatures = hasParking || facilities.length > 0;

  if (!hasFeatures) return null;

  return (
    <ul className="divide-dx-border-default divide-y py-[8px]">
      {hasParking && (
        <FeatureRow
          Icon={DirectionsCarIcon}
          label={parkingCapacity ? `駐車場 ${parkingCapacity}` : "駐車場"}
          description={parkingInfo}
          trailing={
            // 駐車場: ⓘ タップで拠点案内図をモーダル表示 (PIL-437 item10.3)。
            mapImageUrl ? (
              <DxImageInfoButton
                imageUrl={mapImageUrl}
                title="拠点案内図"
                ariaLabel="駐車場の詳細を開く"
                data-analytics-id="site_detail_opened_site_map"
              />
            ) : undefined
          }
        />
      )}
      {facilities.map((facility) => (
        <FeatureRow
          key={facility.id}
          Icon={FACILITY_ICONS[facility.type] ?? InfoIcon}
          label={facility.name}
          description={facility.description}
          trailing={
            // 付帯サービス: ⓘ タップで設備の詳細 (写真/説明/料金) をモーダル表示。
            // trigger は Radix Dialog.Trigger(asChild) が ref を渡すため素の <button>
            // にする (DxIconButton は forwardRef 非対応)。 見た目は駐車場 ⓘ に合わせる。
            <FacilityDetailModal
              facilityData={facility}
              trigger={
                <button
                  type="button"
                  aria-label={`${facility.name}の詳細を開く`}
                  data-analytics-id="site_detail_opened_facility_detail"
                  className="group inline-flex size-[32px] flex-none items-center justify-center outline-none focus:outline-none focus-visible:outline-none"
                >
                  <span className="group-hover:bg-dx-bg-base-hover group-focus-visible:ring-dx-border-focus text-dx-text-default inline-flex size-full items-center justify-center rounded-full bg-transparent transition-colors duration-150 group-focus-visible:ring-[1.5px]">
                    <span className="inline-flex size-[20px] shrink-0 items-center justify-center">
                      <InfoIcon />
                    </span>
                  </span>
                </button>
              }
            />
          }
        />
      ))}
    </ul>
  );
}
